import * as React from "react";
import { useFormApi } from "informedv4";
import { Dropdown } from "../../../../forms/informedv4/DropdownV4";
import { EntityLabel } from "./EntityLabelCorrectionWizard";
import { Required } from "../../../../forms/validation/Required";
import { Subject, StudyScheduleVisit, StudyScheduleSession } from "webportalclient";
import Loading, { FontSize } from "../../../../misc/components/loading";

interface EntityLabelSelectorProps {
    subjectIdFieldDisplayText: string;
    originalSubjectValue: EntityLabel;
    originalVisitValue: EntityLabel;
    originalSessionValue: EntityLabel;
    possibleSubjects: Subject[];
    possibleVisits: StudyScheduleVisit[];
    possibleSessions: StudyScheduleSession[];
    loading: boolean;

    onVisitChange: (value) => void;
    onNext: (form: EntityLabelSelectorForm) => void;
}

export interface EntityLabelSelectorForm {
    Subject: string;
    Visit: string;
    Session: string;
}

export const EntityLabelSelector = ({
    subjectIdFieldDisplayText,
    originalSubjectValue,
    originalVisitValue,
    originalSessionValue,
    possibleSubjects,
    possibleVisits,
    possibleSessions,
    loading,
    onVisitChange,
    onNext
}: EntityLabelSelectorProps): JSX.Element => {
    const formApi = useFormApi();

    const onSubmit = () => {
        formApi.validate();

        const state = formApi.getFormState()
        if (state.invalid)
            return;

        onNext(state.values as unknown as EntityLabelSelectorForm);
    }

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="col-md-12">
                        <p className="text-muted" />
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>Current {subjectIdFieldDisplayText}</label>
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                readOnly
                                value={originalSubjectValue.label}
                            />
                        </div>
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>Current Visit Label</label>
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                readOnly
                                value={originalVisitValue.label}
                            />
                        </div>
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>Current Session Label</label>
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                readOnly
                                value={originalSessionValue.label}
                            />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>New {subjectIdFieldDisplayText}</label>&nbsp;
                            <Dropdown
                                name="Subject"
                                id="Subject"
                                field="Subject"
                                className="form-control"
                                disabled={possibleSubjects.length === 0}
                                onChange={e => {
                                    formApi.setValue("Visit", null);
                                    formApi.setValue("Session", null);
                                }}
                            >
                                {possibleSubjects.map(s => (
                                    <option key={`SubjectOption${s.id}`} value={s.id}>
                                        {s.number}
                                    </option>
                                ))}
                            </Dropdown>
                        </div>
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>New Visit Label</label>
                            {loading ? (
                                <Loading
                                    className="ml-1"
                                    style={{ display: "inline-block" }}
                                    size={FontSize.OneX}
                                />
                            ) : null}
                            <Dropdown
                                name="Visit"
                                id="Visit"
                                field="Visit"
                                className="form-control"
                                disabled={possibleVisits.length === 0 || loading}
                                onChange={e => {
                                    onVisitChange((e as any).target.value);
                                    formApi.setValue("Session", null);
                                }}
                                validate={Required}
                            >
                                <option key={`VisitOptionBlank`} value={null} />
                                {possibleVisits.map(v => (
                                    <option key={`VisitOption${v.id}`} value={v.id}>
                                        {v.label}
                                    </option>
                                ))}
                            </Dropdown>
                        </div>
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>New Session Label</label>
                            {loading ? (
                                <Loading
                                    className="ml-1"
                                    style={{ display: "inline-block" }}
                                    size={FontSize.OneX}
                                />
                            ) : null}
                            <Dropdown
                                name="Session"
                                id="Session"
                                field="Session"
                                disabled={!formApi.getValue("Visit") || possibleSessions.length === 0 || loading}
                                className="form-control"
                                validate={Required}
                            >
                                <option key={`SessionOptionBlank`} value={null} />
                                {possibleSessions.map(s => (
                                    <option key={`SessionOption${s.id}`} value={s.id}>
                                        {s.label}
                                    </option>
                                ))}
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal-footer">
                <button className="btn btn-primary" type="submit" onClick={() => onSubmit()}>
                    Next
                </button>
            </div>
        </>
    );
};
